import { ExperimentCookieName, ExperimentName, ExperimentValue, type ExperimentValueUnion } from '@core/types'
import type { AssetAbManifest, AssetAbTest, AssetAbVariant, GameAssetTypesUnion, UseAssetAbTestsReturn } from '@game/types'
import { readonly, ref, useExperiment, useExperimentAssignment, useExperimentVersions, useNuxtApp, useState } from '#imports'
import { ASSET_AB_STATE_KEY, assetAbTestsFor, rememberAssetAbTests, resolveAssetVariant } from '../utils/assetAbVariant'

const INERT: UseAssetAbTestsReturn = {
    enabled: false,
    bucket: readonly(ref<ExperimentValueUnion>(ExperimentValue.A)),
    activeTest: () => undefined,
    variantFor: () => null,
    trackingMeta: () => ({}),
}

function _useAssetAbTests(): UseAssetAbTestsReturn {
    if (!useExperimentVersions(ExperimentName.GAME_ASSET_AB)) return INERT

    const manifest = useState<AssetAbManifest | null>(ASSET_AB_STATE_KEY, () => null)
    const bucket = useExperiment({
        cookieName: ExperimentCookieName.GAME_ASSET_AB,
        experimentName: ExperimentName.GAME_ASSET_AB,
    })
    const assignment = useExperimentAssignment({
        cookieName: ExperimentCookieName.GAME_ASSET_AB,
        experimentName: ExperimentName.GAME_ASSET_AB,
    })

    function activeTest(slug: string): AssetAbTest | undefined {
        return manifest.value?.tests[slug]
    }

    function variantFor(slug: string, type: GameAssetTypesUnion, siteFolder: string): AssetAbVariant | null {
        return resolveAssetVariant(manifest.value, bucket.value, slug, type, siteFolder)
    }

    // The assigned bucket is stamped whenever the game is under test, whichever file the tile
    // ended up rendering: `a` is the control group, absence means untested or not in the test.
    function trackingMeta(slug: string): { variant?: ExperimentValueUnion } {
        return activeTest(slug) && assignment.value ? { variant: assignment.value.variant } : {}
    }

    return { enabled: true, bucket, activeTest, variantFor, trackingMeta }
}

/**
 * The visitor's asset A/B bucket and the active tests for this page-view.
 *
 * Memoised per Nuxt app rather than `createSharedComposable`: it is read from
 * every tile and from click handlers, and must not be disposed when the last
 * tile unmounts. The tracking utils read the same instance via `assetAbTestsFor`.
 */
export function useAssetAbTests(): UseAssetAbTestsReturn {
    const nuxtApp = useNuxtApp()

    return assetAbTestsFor(nuxtApp) ?? rememberAssetAbTests(nuxtApp, _useAssetAbTests())
}
